import * as Sentry from '@sentry/react';

import { type MonitoringConfig, MonitoringConfigSchema } from '../config/schemas/monitoring';
import { logger } from '../utils/logger';

class MonitoringService {
  private static instance: MonitoringService | null = null;
  private _initialized = false;
  private static initializationError: Error | null = null;

  private constructor() {
    logger.debug({ component: 'monitoring' }, 'MonitoringService constructor called');
  }

  static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      logger.debug({ component: 'monitoring' }, 'Creating new MonitoringService instance');
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  init(config: MonitoringConfig): boolean {
    logger.debug({ component: 'monitoring' }, 'MonitoringService init called. Current state:', {
      initialized: this._initialized,
      hasError: !!MonitoringService.initializationError,
      config: {
        ...config,
        dsn: config.dsn ? '[MASKED]' : 'Not configured',
      },
    });

    if (this._initialized) {
      logger.warn({ component: 'monitoring' }, 'MonitoringService already initialized');
      return true;
    }

    try {
      // Validate config
      logger.debug({ component: 'monitoring' }, 'Validating config...');
      const validatedConfig = MonitoringConfigSchema.parse(config);
      logger.debug({ component: 'monitoring' }, 'Config validation result:', {
        ...validatedConfig,
        dsn: validatedConfig.dsn ? '[MASKED]' : 'Not configured',
        environment: validatedConfig.environment,
        debug: validatedConfig.debug,
        sampleRate: validatedConfig.sampleRate,
      });

      // Check if DSN is empty or not provided
      if (!validatedConfig.dsn) {
        const error = new Error('Sentry DSN is required but not provided');
        logger.error({ component: 'monitoring' }, 'DSN validation failed:', error);
        MonitoringService.initializationError = error;
        return false;
      }

      logger.debug({ component: 'monitoring' }, 'Initializing Sentry with config:', {
        environment: validatedConfig.environment,
        debug: validatedConfig.debug,
        tracesSampleRate: validatedConfig.sampleRate,
        dsn: '[MASKED]',
      });

      // Initialize Sentry
      Sentry.init({
        dsn: validatedConfig.dsn,
        environment: validatedConfig.environment,
        release: import.meta.env['VITE_SENTRY_RELEASE'] as string | undefined,
        debug: validatedConfig.debug,
        tracesSampleRate: validatedConfig.sampleRate,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        // Session Replay sample rates
        replaysSessionSampleRate: validatedConfig.replaysSessionSampleRate,
        replaysOnErrorSampleRate: validatedConfig.replaysOnErrorSampleRate,
        // Performance monitoring configuration
        tracePropagationTargets: [
          ...validatedConfig.tracingOrigins,
          /^\/api/, // Monitor all API calls (regex — can't be serialized in config)
        ],
        beforeSend(event) {
          if (validatedConfig.debug) {
            const eventData = JSON.parse(JSON.stringify(event)) as Record<string, unknown>;
            logger.debug({ component: 'monitoring' }, 'Sentry event being sent', { eventData });
          }
          return event;
        },
      });

      this._initialized = true;
      MonitoringService.initializationError = null;
      logger.debug({ component: 'monitoring' }, 'Sentry initialized successfully. Service state:', {
        initialized: this._initialized,
        hasError: false,
        environment: validatedConfig.environment,
      });
      return true;
    } catch (error) {
      logger.error(
        { component: 'monitoring' },
        'Failed to initialize Sentry',
        error instanceof Error ? error : new Error('Unknown error'),
      );
      MonitoringService.initializationError =
        error instanceof Error ? error : new Error('Unknown error');
      this._initialized = false;
      return false;
    }
  }

  isInitialized(): boolean {
    const status = {
      initialized: this._initialized,
      hasError: !!MonitoringService.initializationError,
      instance: !!MonitoringService.instance,
      error: MonitoringService.initializationError?.message,
    };
    logger.debug(
      { component: 'monitoring' },
      'MonitoringService.isInitialized() called. Status:',
      status,
    );
    return this._initialized;
  }

  getInitializationError(): Error | null {
    return MonitoringService.initializationError;
  }

  captureError(error: Error, context?: Record<string, unknown>): void {
    if (!this._initialized) {
      logger.error({ component: 'monitoring' }, 'MonitoringService not initialized. Error:', error);
      return;
    }

    logger.debug({ component: 'monitoring' }, 'Capturing error:', {
      error,
      context,
    });
    Sentry.withScope(scope => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(error);
    });
  }

  captureMessage(message: string, context?: Record<string, unknown>): void {
    if (!this._initialized) {
      logger.error(
        { component: 'monitoring' },
        'MonitoringService not initialized',
        new Error(`Failed to capture message: ${message}`),
      );
      return;
    }

    logger.debug({ component: 'monitoring' }, 'Capturing message', {
      message,
      context,
    });
    Sentry.withScope(scope => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureMessage(message);
    });
  }

  setUser(id: string, email?: string, username?: string): void {
    if (!this._initialized) {
      logger.error(
        { component: 'monitoring' },
        `MonitoringService not initialized. Cannot set user: ${id}`,
      );
      return;
    }

    logger.debug({ component: 'monitoring' }, 'Setting user:', {
      id,
      email,
      username,
    });
    Sentry.setUser({
      id,
      email,
      username,
    });
  }
}

// Create and export a single instance
export const monitoringService = MonitoringService.getInstance();
